import * as THREE from "three";
import type { NeonTube } from "../neon/NeonTube";
import type { Tool, ToolContext, PointerInfo } from "./Tool";

const HANDLE_RADIUS = 0.09;
const HANDLE_PICK_RADIUS = 0.16;

/**
 * Control-point editor. Shows a handle at every tube vertex; drag to
 * reposition. Alt-click a segment inserts a new handle midway. Backspace
 * while hovering a handle deletes it.
 */
export class BendTool implements Tool {
  readonly id = "bend";
  readonly name = "Bend";
  readonly grabsPointer = true;
  readonly hint = "Drag any handle to bend the tube. Alt-click a tube to insert a point.";
  readonly icon = `
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M3 17c4 0 6-10 10-10s6 10 8 10" />
      <circle cx="3" cy="17" r="1.4" fill="currentColor" />
      <circle cx="13" cy="7" r="1.4" fill="currentColor" />
      <circle cx="21" cy="17" r="1.4" fill="currentColor" />
    </svg>`;

  private handleGroup = new THREE.Group();
  private handleGeom = new THREE.SphereGeometry(HANDLE_RADIUS, 16, 12);
  private handleMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.95,
    depthTest: false,
    toneMapped: false,
  });
  private hoverHandle: THREE.Mesh | null = null;
  private dragging: { tube: NeonTube; pointIndex: number } | null = null;
  private hoverLastPoint: THREE.Vector3 | null = null;

  constructor() {
    this.handleGroup.name = "bend-handles";
    this.handleGroup.renderOrder = 999;
  }

  onActivate(ctx: ToolContext): void {
    ctx.scene.add(this.handleGroup);
    this.rebuildHandles(ctx);
    this.unsubscribe = ctx.design.subscribe(() => this.rebuildHandles(ctx));
  }

  private unsubscribe: (() => void) | null = null;

  onDeactivate(ctx: ToolContext): void {
    ctx.scene.remove(this.handleGroup);
    this.clearHandles();
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  onPointerDown(info: PointerInfo, ctx: ToolContext): void {
    const hit = this.pickHandle(info.raycaster);
    if (hit) {
      this.dragging = hit.handle.userData as { tube: NeonTube; pointIndex: number };
      this.hoverLastPoint = hit.point.clone();
      return;
    }
    if (info.event.altKey) {
      this.insertPoint(info, ctx);
    }
  }

  onPointerMove(info: PointerInfo, ctx: ToolContext): void {
    if (this.dragging) {
      if (!info.planePoint) return;
      this.dragging.tube.setPoint(this.dragging.pointIndex, info.planePoint);
      this.hoverLastPoint = info.planePoint.clone();
      // Move the visual handle with the point.
      this.rebuildHandles(ctx);
      return;
    }
    const hit = this.pickHandle(info.raycaster);
    this.setHover(hit?.handle ?? null);
  }

  onPointerUp(_info: PointerInfo, ctx: ToolContext): void {
    if (this.dragging) ctx.design.touch();
    this.dragging = null;
  }

  onKeyDown(ev: KeyboardEvent, ctx: ToolContext): void {
    if (ev.key !== "Backspace" && ev.key !== "Delete") return;
    if (!this.hoverHandle) return;
    const { tube, pointIndex } = this.hoverHandle.userData as { tube: NeonTube; pointIndex: number };
    if (tube.points.length <= 2) {
      ctx.design.remove(tube);
    } else {
      tube.removePoint(pointIndex);
    }
    ctx.design.touch();
    ev.preventDefault();
  }

  private insertPoint(info: PointerInfo, ctx: ToolContext): void {
    if (!info.planePoint) return;
    // Find the nearest tube segment to this click.
    let best: { tube: NeonTube; insertAt: number; dist: number } | null = null;
    for (const tube of ctx.design.getTubes()) {
      if (!tube.renderable) continue;
      for (let i = 0; i < tube.points.length - 1; i++) {
        const a = tube.points[i];
        const b = tube.points[i + 1];
        const d = distancePointToSegment(info.planePoint, a, b);
        if (!best || d < best.dist) best = { tube, insertAt: i + 1, dist: d };
      }
    }
    if (!best || best.dist > 0.35) return;
    best.tube.points.splice(best.insertAt, 0, info.planePoint.clone());
    best.tube.rebuildMesh();
    ctx.design.touch();
  }

  private pickHandle(raycaster: THREE.Raycaster): { handle: THREE.Mesh; point: THREE.Vector3 } | null {
    raycaster.params.Mesh = { threshold: HANDLE_PICK_RADIUS };
    const hits = raycaster.intersectObjects(this.handleGroup.children, false);
    if (!hits.length) return null;
    return { handle: hits[0].object as THREE.Mesh, point: hits[0].point };
  }

  private setHover(next: THREE.Mesh | null): void {
    if (this.hoverHandle === next) return;
    if (this.hoverHandle) (this.hoverHandle.material as THREE.MeshBasicMaterial).opacity = 0.95;
    this.hoverHandle = next;
    if (next) (next.material as THREE.MeshBasicMaterial).opacity = 0.4;
  }

  private clearHandles(): void {
    for (const child of this.handleGroup.children.slice()) {
      this.handleGroup.remove(child);
    }
    this.hoverHandle = null;
  }

  private rebuildHandles(ctx: ToolContext): void {
    this.clearHandles();
    for (const tube of ctx.design.getTubes()) {
      for (let i = 0; i < tube.points.length; i++) {
        const h = new THREE.Mesh(this.handleGeom, this.handleMat.clone());
        h.position.copy(tube.points[i]);
        h.userData = { tube, pointIndex: i };
        this.handleGroup.add(h);
      }
    }
    // Silence unused warning when not dragging.
    void this.hoverLastPoint;
  }
}

function distancePointToSegment(p: THREE.Vector3, a: THREE.Vector3, b: THREE.Vector3): number {
  const ab = b.clone().sub(a);
  const ap = p.clone().sub(a);
  const lenSq = ab.lengthSq();
  if (lenSq === 0) return ap.length();
  const t = Math.max(0, Math.min(1, ap.dot(ab) / lenSq));
  const closest = a.clone().add(ab.multiplyScalar(t));
  return p.distanceTo(closest);
}
